// src/pages/Schedule.js
import React, { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { FiUser, FiHome } from 'react-icons/fi';

// This is a required CSS file for the calendar's styling
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Set up the localizer for dates
const localizer = momentLocalizer(moment);

const Schedule = ({ appointments, onUpdateAppointment, tickets, technicians, communities }) => {
    const navigate = useNavigate();

    // Custom component to render each event as a "mini ticket card"
    const CustomEventComponent = useCallback(({ event }) => {
        const appointment = event.resource;
        if (!appointment) return null;

        const ticket = tickets.find(t => {
            const ticketId = typeof appointment.ticketId === 'object' ? appointment.ticketId._id : appointment.ticketId;
            return t._id === ticketId;
        });

        const community = communities.find(c => c._id === appointment.communityId);

        const assignedTechnicians = technicians.filter(tech => 
            appointment.technicianIds.includes(tech._id)
        );

        return (
            <div className="event-card-wrapper">
                <div className={`event-card-priority-bar priority-${ticket?.priority?.toLowerCase() || 'low'}`}></div>
                <div className="event-card-content">
                    <div className="event-card-header">
                        <span className="event-card-title">{event.title}</span>
                    </div>
                    <div className="event-card-details">
                        {community && (
                            <div className="event-detail-item">
                                <FiHome size={12} className="event-detail-icon" />
                                <span>{community.name}</span>
                            </div>
                        )}
                        {assignedTechnicians.length > 0 && (
                            <div className="event-detail-item">
                                <FiUser size={12} className="event-detail-icon" />
                                <div className="event-tech-avatars">
                                    {assignedTechnicians.map(tech => (
                                        <div key={tech._id} className="tech-avatar-sm" title={tech.name}>
                                            {tech.name ? tech.name.charAt(0) : '?'}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }, [tickets, technicians, communities]);

    // Memoize the transformation of appointments into events.
    const events = useMemo(() => {
        if (!appointments) return [];
        return appointments.map(app => ({
            id: app._id,
            title: app.title,
            start: new Date(app.startTime),
            end: new Date(app.endTime),
            resource: app, 
        }));
    }, [appointments]);

    const handleSelectEvent = (event) => {
        if (event.resource && event.resource.ticketId) {
            const ticketIdValue = typeof event.resource.ticketId === 'object'
                ? event.resource.ticketId._id
                : event.resource.ticketId;

            if (ticketIdValue) {
                navigate(`/tickets/${ticketIdValue}`);
            }
        }
    };
    
    const handleEventDrop = ({ event, start, end }) => {
        const updatedData = {
            startTime: start.toISOString(),
            endTime: end.toISOString(),
        };
        onUpdateAppointment(event.id, updatedData);
    };

    const handleEventResize = ({ event, start, end }) => {
        const updatedData = {
            startTime: start.toISOString(),
            endTime: end.toISOString(),
        };
        onUpdateAppointment(event.id, updatedData);
    };
    
    const components = useMemo(() => ({
        event: CustomEventComponent,
    }), [CustomEventComponent]);

    const eventPropGetter = useCallback(() => ({
        className: 'custom-calendar-event'
    }), []);

    return (
        <div className="page-container" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div className="page-header">
                <div>
                    <h2 className="page-title">Service Schedule</h2>
                    <p className="page-subtitle">View, manage, and reschedule all service appointments.</p>
                </div>
            </div>
            
            {/* ✅ UPDATED: Wrapped in Card for Visual Consistency */}
            <div className="content-area" style={{ flexGrow: 1, padding: '0 1.5rem 1.5rem 1.5rem', display: 'flex', flexDirection: 'column' }}>
                <div className="card" style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div className="card-body" style={{ flexGrow: 1, padding: '1rem' }}>
                        <Calendar
                            localizer={localizer}
                            events={events}
                            startAccessor="start"
                            endAccessor="end"
                            style={{ height: '100%', width: '100%' }}
                            onSelectEvent={handleSelectEvent}
                            onEventDrop={handleEventDrop} 
                            onEventResize={handleEventResize}
                            components={components}
                            eventPropGetter={eventPropGetter}
                            views={['month', 'week', 'day', 'agenda']} 
                            selectable 
                            resizable 
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Schedule;